import React from "react"
import { Form, Input, Select } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { useEngine } from "hooks/api/useEngine"
import { Project } from "stores/projectStore" // Importing Project type from Zustand store
import { SelectOption } from "types/ui.types"
import { Variant } from "types/questionnaire.types"
import { EXPORT_VARIABLE_TYPES, VARIABLE_STYLE } from "types/engineExport.types"

/**
 * Form values interface for the Decipher export form.
 */
interface DecipherFormValues {
  variant: string
  languages: string // Still single string (single-select), but field name matches API
  filename: string
  "xml-notes"?: string
  "qnr-notes"?: string
  "variable-names"?: EXPORT_VARIABLE_TYPES
  "label-style"?: VARIABLE_STYLE
}

/**
 * Props interface for the ExportToDecipherOptionsForm component.
 * Uses the Project type from the Zustand store.
 */
interface ExportToDecipherOptionsFormProps {
  project: Project // Now using your actual Project type from the store
  setExportDecipherModalVisible: (visible: boolean) => void
  variants: Variant[]
  getQuestionnaireNotes: () => string[]
}

/**
 * ExportToDecipherOptionsForm component renders a form for configuring options
 * to export a project to Decipher XML format.
 *
 * @param props - The component props.
 * @returns The rendered ExportToDecipherOptionsForm component.
 */
const ExportToDecipherOptionsForm: React.FC<
  ExportToDecipherOptionsFormProps
> = (props) => {
  const {
    project,
    setExportDecipherModalVisible,
    variants,
    getQuestionnaireNotes,
  } = props
  const [form] = Form.useForm<DecipherFormValues>()
  const { convertToXMLDecipher, isConvertingToXMLDecipher } = useEngine()

  /**
   * Handles the form submission and triggers the export to Decipher XML.
   *
   * @async
   * @function submitDecipherOptions
   * @param formValues - The values from the form fields.
   * @returns A promise that resolves when the export is complete.
   */
  const submitDecipherOptions = async (
    formValues: DecipherFormValues
  ): Promise<void> => {
    const { variant, languages, filename } = formValues
    const xmlNotes = formValues["xml-notes"] || "" // Default to an empty string if not selected
    const qnrNotes = formValues["qnr-notes"] || "" // Default to an empty string if not selected
    const variableNames =
      formValues["variable-names"] || EXPORT_VARIABLE_TYPES.NUMBERING
    const labelStyle = formValues["label-style"] || VARIABLE_STYLE.DEFAULT

    try {
      await convertToXMLDecipher(project.id, {
        variant,
        languages,
        filename,
        "xml-notes": xmlNotes,
        "qnr-notes": qnrNotes,
        "variable-names": variableNames,
        "label-style": labelStyle,
      })

      notify.toast.success("Exported to Decipher XML successfully!")
      hideModal()
    } catch (error) {
      notify.toast.error("Failed to export to Decipher XML.")
      console.error("Error exporting to Decipher XML:", error)
      hideModal()
    }
  }

  /**
   * Resets the form fields and hides the modal.
   *
   * @function hideModal
   */
  const hideModal = (): void => {
    form.resetFields()
    setExportDecipherModalVisible(false)
  }

  /**
   * Preprocesses the notes array to remove duplicates and format it
   * as options for use in the Select component.
   *
   * @param notes - The array of notes to preprocess.
   * @returns The formatted options array for the Select component.
   */
  const preprocessNotes = (notes: string[]): SelectOption[] => {
    // Remove duplicates using Set and map to the desired structure
    const uniqueNotes = [...new Set(notes)].map((note) => ({
      label: note,
      value: note,
    }))
    return uniqueNotes
  }

  /**
   * Extracts unique languages from an array of variants and a project object.
   * @param variants - An array of variant objects.
   * @param project - A project object containing languages.
   * @returns An array of unique languages.
   */
  const getUniqueLanguages = (
    variants: Variant[],
    project: Project
  ): string[] => {
    const variantLanguages = variants.flatMap((variant) => variant.languages)
    const allLanguages = [...variantLanguages, ...project.Languages]
    return [...new Set(allLanguages)]
  }

  // Default to MASTER if variants array is empty, or if only one language, default it
  const variantOptions: SelectOption[] =
    variants.length > 0
      ? variants.map((variant) => ({
          label: variant.name,
          value: variant.name,
        }))
      : [{ label: "MASTER", value: "MASTER" }]

  const defaultVariant: string | undefined =
    variantOptions.length === 1 ? variantOptions[0].value : undefined

  const languageOptions: SelectOption[] = getUniqueLanguages(
    variants,
    project
  ).map((language) => ({
    label: language,
    value: language,
  }))

  const defaultLanguages: string | undefined =
    languageOptions.length === 1 ? languageOptions[0].value : undefined

  // Get notes and preprocess them
  const notes = getQuestionnaireNotes()
  const processedNotes = preprocessNotes(notes)

  return (
    <Form
      form={form}
      name="export_to_decipher_options"
      onFinish={submitDecipherOptions}
      style={{ textAlign: "left" }}
      layout="vertical"
      initialValues={{
        variant: defaultVariant,
        languages: defaultLanguages,
        "variable-names": EXPORT_VARIABLE_TYPES.NUMBERING,
        "label-style": VARIABLE_STYLE.DEFAULT,
      }}
    >
      <Form.Item
        style={{ marginBottom: "1em" }}
        label="Please enter a filename"
        name="filename"
        validateTrigger="onSubmit"
        rules={[
          {
            required: true,
            message: "Please enter a filename",
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        style={{ marginBottom: "1em" }}
        label="Variant"
        name="variant"
        validateTrigger="onSubmit"
        rules={[
          {
            required: true,
            message: "Please select a variant",
          },
        ]}
      >
        <Select placeholder="Select Variant" options={variantOptions} />
      </Form.Item>
      <Form.Item
        style={{ marginBottom: "1em" }}
        label="Languages"
        name="languages"
        validateTrigger="onSubmit"
        rules={[
          {
            required: true,
            message: "Please select a language",
          },
        ]}
      >
        <Select placeholder="Select Language" options={languageOptions} />
      </Form.Item>
      <Form.Item
        style={{ marginBottom: "1em" }}
        label="Embed Notes in the XML file"
        name="xml-notes"
        validateTrigger="onSubmit"
      >
        <Select options={processedNotes} />
      </Form.Item>
      <Form.Item
        style={{ marginBottom: "1em" }}
        label="For testing, show notes in questionnaire"
        name="qnr-notes"
        validateTrigger="onSubmit"
      >
        <Select options={processedNotes} />
      </Form.Item>
      <Form.Item
        style={{ marginBottom: "1em" }}
        label="Use as variable names"
        name="variable-names"
        validateTrigger="onSubmit"
      >
        <Select
          defaultValue={EXPORT_VARIABLE_TYPES.NUMBERING}
          options={[
            {
              value: EXPORT_VARIABLE_TYPES.NUMBERING,
              label: "Numbered automatically",
            },
            {
              value: EXPORT_VARIABLE_TYPES.LABELS,
              label: "Generate from questionnaire labels",
            },
          ]}
        />
      </Form.Item>
      <Form.Item
        style={{ marginBottom: "1em" }}
        label="Variable style"
        name="label-style"
        validateTrigger="onSubmit"
      >
        <Select
          defaultValue={VARIABLE_STYLE.DEFAULT}
          options={[
            {
              value: VARIABLE_STYLE.DEFAULT,
              label: "Default (s/p prefixes)",
            },
            {
              value: VARIABLE_STYLE.DECIPHER,
              label: "Decipher standard (r/c prefixes)",
            },
          ]}
        />
      </Form.Item>

      <div className="flex flex-row mt-5">
        <div className="ml-auto mr-2 space-x-2">
          <DataQButton onClick={hideModal} type="primary">
            Cancel
          </DataQButton>
          <DataQButton
            type="primary"
            htmlType="submit"
            isLoading={isConvertingToXMLDecipher}
          >
            Export
          </DataQButton>
        </div>
      </div>
    </Form>
  )
}

export default ExportToDecipherOptionsForm
